// Shared "copy link" affordance used by v2 share rows on both the Event
// overview page and the Thankyou confirmation page. Wires every
// [data-share-copy] button under `root` to copy its data-share-url to the
// clipboard, flash a green check + "Link copied" tooltip for ~1.5s, then
// revert. If the host browser doesn't support navigator.clipboard the
// button is removed so we don't surface a non-working control. Each button is
// wired once, so markup added later can be passed through again safely.

const FEEDBACK_MS = 1500

const supportsClipboard = () =>
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'

export function initCopyShareButtons(root) {
    const scope = root || document
    const buttons = scope.querySelectorAll('[data-share-copy]')
    if (buttons.length === 0) return

    if (!supportsClipboard()) {
        buttons.forEach((btn) => btn.remove())
        return
    }

    buttons.forEach((btn) => {
        if (btn.dataset.shareCopyWired) return
        btn.dataset.shareCopyWired = '1'
        const url = btn.getAttribute('data-share-url') || ''
        // Capture idle text once, before a flash can overwrite it — otherwise a
        // rapid second click captures "Link copied" and it sticks.
        const wrapper = btn.parentElement
        const tooltip = wrapper && wrapper.querySelector('[data-share-copy-feedback]')
        const textEl = tooltip && tooltip.querySelector('[data-share-copy-text]')
        const state = { idleText: textEl ? textEl.textContent : '', timer: null }
        btn.addEventListener('click', () => {
            navigator.clipboard.writeText(url).then(
                () => flashCopyFeedback(btn, state),
                () => {
                    // Clipboard write rejected (permission policy etc.) — fail
                    // silently; the user can still long-press / use the other
                    // share buttons.
                },
            )
        })
    })
}

function flashCopyFeedback(btn, state) {
    const linkIcon = btn.querySelector('[data-share-copy-icon]')
    const checkIcon = btn.querySelector('[data-share-copy-icon-success]')
    // Tooltip lives outside the <button> so the button's text-color flash
    // doesn't tint it — sibling under the same relative wrapper.
    const wrapper = btn.parentElement
    const tooltip = wrapper && wrapper.querySelector('[data-share-copy-feedback]')
    const textEl = tooltip && tooltip.querySelector('[data-share-copy-text]')
    const idleText = state.idleText

    if (state.timer !== null) window.clearTimeout(state.timer)

    // Clears the legacy `hidden` class too, so the v2 tooltip can fade.
    const HIDDEN = ['hidden', 'invisible', 'opacity-0', 'translate-y-1']
    const SHOWN = ['visible', 'opacity-100', 'translate-y-0']

    if (linkIcon) linkIcon.classList.add('hidden')
    if (checkIcon) checkIcon.classList.remove('hidden')
    if (textEl) textEl.textContent = 'Link copied'
    if (tooltip) {
        tooltip.classList.remove(...HIDDEN)
        tooltip.classList.add(...SHOWN)
    }

    state.timer = window.setTimeout(() => {
        state.timer = null
        if (linkIcon) linkIcon.classList.remove('hidden')
        if (checkIcon) checkIcon.classList.add('hidden')
        if (tooltip) {
            tooltip.classList.remove(...SHOWN)
            tooltip.classList.add('invisible', 'opacity-0', 'translate-y-1')
        }
        if (textEl) textEl.textContent = idleText
    }, FEEDBACK_MS)
}
